import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../../context/UserContext';
import { supabase } from '../../lib/supabase';
import { Loader2 } from 'lucide-react';
import { useChat } from './ChatContext';
import type { ChatChannel } from './useChatChannels';
import { Avatar, Badge, Icon, IconButton, Popover } from '../ui';

const OWLBERT_USER_ID = 'a0000000-0000-0000-0000-000000000001';

interface DmUserInfo {
  id: string;
  name: string;
  avatar: string | null;
}

interface ChannelHeaderProps {
  channel: ChatChannel;
  onClose: () => void;
  onBack?: () => void;
  isMobile?: boolean;
  mode?: 'panel' | 'fullscreen';
  onToggleSidebar?: () => void;
  isSidebarCollapsed?: boolean;
}

export default function ChannelHeader({
  channel,
  onClose,
  onBack,
  isMobile,
  mode = 'panel',
  onToggleSidebar,
  isSidebarCollapsed,
}: ChannelHeaderProps) {
  const { user } = useUser();
  const navigate = useNavigate();
  const { triggerChannelRefresh, setSelectedChannelId } = useChat();
  const [memberCount, setMemberCount] = useState(0);
  const [dmUser, setDmUser] = useState<DmUserInfo | null>(null);
  const [showSettings, setShowSettings] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const settingsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const fetchMemberCount = async () => {
      const { count } = await supabase
        .from('chat_channel_members')
        .select('*', { count: 'exact', head: true })
        .eq('channel_id', channel.id);
      setMemberCount(count || 0);
    };

    fetchMemberCount();
  }, [channel.id]);

  // For DM channels, resolve the other user
  useEffect(() => {
    if (channel.type !== 'direct' || !user) {
      setDmUser(null);
      return;
    }

    const fetchDmUser = async () => {
      // Get the other member
      const { data: members } = await supabase
        .from('chat_channel_members')
        .select('user_id')
        .eq('channel_id', channel.id)
        .neq('user_id', user.id);

      if (!members || members.length === 0) {
        setDmUser(null);
        return;
      }

      const otherUserId = members[0].user_id;

      // Fetch user details
      const { data: userData } = await supabase
        .from('users')
        .select('id, name, avatar')
        .eq('id', otherUserId)
        .single();

      if (userData) {
        setDmUser({
          id: userData.id,
          name: userData.name || 'Unknown',
          avatar: userData.avatar || null,
        });
      }
    };

    fetchDmUser();
  }, [channel.id, channel.type, user]);

  const handleArchiveChannel = async () => {
    if (archiving) return;
    setArchiving(true);
    try {
      const { error: updateError } = await supabase
        .from('chat_channels')
        .update({ archived: true })
        .eq('id', channel.id);

      if (updateError) {
        console.error('Error archiving channel:', updateError);
        return;
      }

      setShowSettings(false);
      triggerChannelRefresh();
      setSelectedChannelId(null);
    } catch (err) {
      console.error('Error archiving channel:', err);
    } finally {
      setArchiving(false);
    }
  };

  const isDm = channel.type === 'direct';
  const isAdmin = user?.role === 'admin' || user?.role === 'owner';
  const displayName = isDm && dmUser ? dmUser.name : channel.name;

  const renderAvatar = () => {
    if (!isDm || !dmUser) {
      return (
        <span className="flex-shrink-0" style={{ color: 'var(--text-faint)' }}>
          {channel.type === 'client' ? (
            <Icon name="building-2" size={15} />
          ) : (
            <span style={{ fontSize: 'var(--text-base)' }}>#</span>
          )}
        </span>
      );
    }

    // Owlbert
    if (dmUser.id === OWLBERT_USER_ID) {
      return (
        <span className="w-6 h-6 flex items-center justify-center flex-shrink-0">
          <span style={{ fontSize: '16px' }}>🦉</span>
        </span>
      );
    }

    return <Avatar name={dmUser.name} src={dmUser.avatar} size={24} />;
  };

  return (
    <div
      className="flex items-center justify-between flex-shrink-0"
      style={{
        padding: '10px 14px',
        borderBottom: '1px solid var(--border-hairline)',
      }}
    >
      <div className="flex items-center gap-2 min-w-0 flex-1">
        {isMobile && onBack && (
          <IconButton icon="chevron-left" size="sm" label="Back" onClick={onBack} />
        )}

        {/* Show sidebar toggle when collapsed in panel mode */}
        {!isMobile && isSidebarCollapsed && onToggleSidebar && (
          <IconButton
            icon="chevron-right"
            size="sm"
            label="Show channels"
            onClick={onToggleSidebar}
          />
        )}

        {renderAvatar()}

        <div className="min-w-0 flex-1 flex items-center gap-2">
          <h3
            className="truncate"
            style={{
              margin: 0,
              fontSize: 'var(--text-base)',
              fontWeight: 'var(--weight-semibold)' as React.CSSProperties['fontWeight'],
              color: 'var(--text-body)',
            }}
          >
            {displayName}
          </h3>
          {channel.type === 'client' && <Badge tone="info">Client</Badge>}
          {!isDm && channel.description && (
            <p
              className="truncate hidden sm:block"
              style={{ margin: 0, fontSize: 'var(--text-xs)', color: 'var(--text-faint)' }}
            >
              {channel.description}
            </p>
          )}
        </div>
      </div>

      <div className="flex items-center gap-1 flex-shrink-0">
        {/* Member count — hide for DMs (always 2) */}
        {!isDm && (
          <span
            className="t-num inline-flex items-center gap-1"
            style={{
              padding: '0 8px',
              fontSize: 'var(--text-2xs)',
              color: 'var(--text-faint)',
            }}
            title={`${memberCount} members`}
          >
            <Icon name="user" size={11} />
            {memberCount}
          </span>
        )}

        {/* View client link */}
        {channel.type === 'client' && channel.client_id && (
          <IconButton
            icon="external-link"
            size="sm"
            label="View client"
            onClick={() => navigate(`/clients/${channel.client_id}`)}
          />
        )}

        {/* Channel settings (non-DM, admin only) */}
        {!isDm && isAdmin && (
          <div ref={settingsRef} className="relative">
            <IconButton
              icon="settings"
              size="sm"
              label="Channel settings"
              active={showSettings}
              onClick={() => setShowSettings(prev => !prev)}
            />

            <Popover
              open={showSettings}
              anchorEl={settingsRef.current}
              onClose={() => setShowSettings(false)}
              align="right"
              minWidth={184}
              role="menu"
            >
                <button
                  onClick={handleArchiveChannel}
                  disabled={archiving}
                  className="hover-wash pressable w-full flex items-center text-left"
                  style={{
                    gap: '9px',
                    padding: '7px 10px',
                    border: 'none',
                    background: 'transparent',
                    borderRadius: 'var(--radius-sm)',
                    fontFamily: 'var(--font-ui)',
                    fontSize: 'var(--text-sm)',
                    color: 'var(--danger-500)',
                    cursor: archiving ? 'not-allowed' : 'pointer',
                    opacity: archiving ? 0.5 : 1,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {archiving ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Icon name="inbox" size={14} />
                  )}
                  <span>{archiving ? 'Archiving...' : 'Archive Channel'}</span>
                </button>
            </Popover>
          </div>
        )}

        {/* Minimize button (fullscreen mode, desktop) — shrink to panel */}
        {mode === 'fullscreen' && !isMobile && (
          <IconButton
            icon="arrow-down-right"
            size="sm"
            label="Minimize to panel"
            onClick={onClose}
          />
        )}

        {/* Close button (panel mode, desktop only) */}
        {mode === 'panel' && !isMobile && (
          <IconButton icon="x" size="sm" label="Close" onClick={onClose} />
        )}
      </div>
    </div>
  );
}
